import { Alert, Box, Stack } from "@mui/material";
import { GridSortDirection, GridSortModel } from "@mui/x-data-grid-premium";
import { useSnackbar } from "notistack";
import React from "react";
import { useNavigate } from "react-router-dom";
import { CustomDataGrid, MyPaper, TitleHeader } from "../../../components";
import { CurrentView } from "../../../components/boardLayout/components/currentView";
import { SelectBoxOption } from "../../../interfaces";
import { useAppDispatch, useAppSelector } from "../../../redux";
import { useDeleteQuoteMutation, useDuplicateQuoteMutation, useLazyGetQuotesQuery } from "../../../redux/services";
import { closeBackdrop, openBackdrop } from "../../../redux/slices/backdrop";
import { closeConfirmationDialog, openConfirmationDialog } from "../../../redux/slices/confirmationDialog";
import { resetProjectSlice } from "../../../redux/slices/project";
import { HasAccess } from "../../../router/authorization";
import { PERMISSIONS } from "../../../router/permission";
import { AppRoutes } from "../../../router/routes";
import { entityHistoryOptions } from "../../../utils";
import { DashboardTableColumn } from "../utils";

export const Dashboard: React.FC<{ children?: JSX.Element, onPublish: (row: any, refetch: () => void) => void }> = ({ onPublish }) => {

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { perm } = useAppSelector(store => store.auth.userDetails);

    const [getAllQuotes, { data: allData = { data: [], totalCount: 0 }, isFetching, isLoading, isError }] = useLazyGetQuotesQuery({ refetchOnFocus: true });
    const [deleteQuoteMutation] = useDeleteQuoteMutation();
    const [duplicateQuote] = useDuplicateQuoteMutation();

    const [search, setSearch] = React.useState("");
    const [paginationModel, setPaginationModel] = React.useState({ page: 0, pageSize: 100 });
    const [queryOptions, setQueryOptions] = React.useState({ sortModel: [{ field: 'sequence_id', sort: 'desc' as GridSortDirection }] });
    const [dropDown, updateDropDown] = React.useState<SelectBoxOption>(entityHistoryOptions[0]);

    const { data, totalCount } = allData;
    const creatable = React.useMemo(() => HasAccess(perm, PERMISSIONS.COMMERCIAL_IEQUOTE_CREATE), [perm]);
    const deletable = React.useMemo(() => HasAccess(perm, PERMISSIONS.COMMERCIAL_IEQUOTE_DELETE), [perm]);
    const publishable = React.useMemo(() => HasAccess(perm, PERMISSIONS.COMMERCIAL_IEQUOTE_PUBLISH), [perm]);

    const addEditQuote = (data: any, isEdit: boolean = true) => {
        dispatch(resetProjectSlice())
        navigate(AppRoutes.viewQuickQuoteBasicDetail(isEdit ? data._id : "New"))
    };

    const getPath = (id: string) => AppRoutes.viewQuickQuoteBasicDetail(id);

    const deleteQuote = (quote: any) => {
        dispatch(openConfirmationDialog({
            title: "Are you sure?",
            body: `Do you want to delete quote: ${quote.sequence_id}-r${quote.version}`,
            onOk: () => confirmQuoteDelete(quote),
            onNegativeBtn: () => dispatch(closeConfirmationDialog())
        }))
    };

    const confirmQuoteDelete = async (quote: any) => {
        dispatch(openBackdrop("Deleting quote..."));
        let res: any = {};
        try {
            res = await deleteQuoteMutation({ id: quote._id })

            if (Object.keys(res).includes("data")) {
                fetchQuotes();
                enqueueSnackbar(`Deleted quote Successfully!`, { variant: "success" })
                dispatch(closeConfirmationDialog());
            } else if (Object.keys(res).includes("error")) {
                // eslint-disable-next-line 
                throw res.error.data;
            } else {
                // eslint-disable-next-line 
                throw "Data not found"
            }
        } catch (error: any) {
            let errorMessage: string = error?.title ?? "Oops! Something went wrong, Unable to delete"
            enqueueSnackbar(errorMessage, { variant: "error" });
        } finally {
            dispatch(closeBackdrop());
        }
    };

    const giveMeRows = () => {
        if (!isLoading && !isFetching && data) {
            return data ?? [];
        } else {
            return [];
        }
    };

    const fetchQuotes = () => {
        getAllQuotes({
            ...paginationModel,
            sort_by: queryOptions?.sortModel?.[0]?.field,
            sort_order: queryOptions?.sortModel?.[0]?.sort,
            include_histories: dropDown?.value,
            search: search.trim() ? search : ""
        });
    };

    const handleDuplicate = (row: any) => {
        dispatch(openConfirmationDialog({
            title: "Are you sure?",
            body: `Do you want to duplicate the quote: ${row.sequence_id}-r${row.version}?`,
            onOk: () => confirmDuplicate(row),
            onNegativeBtn: () => dispatch(closeConfirmationDialog())
        }))
    };

    const confirmDuplicate = async (row: any) => {
        dispatch(closeConfirmationDialog());
        dispatch(openBackdrop('Duplicating Quote...'));
        await duplicateQuote(row._id).unwrap().then((res: any) => {
            if (res) {
                fetchQuotes();
                enqueueSnackbar('Quote Duplicated Successfully!', { variant: "success" });
            } else {
                enqueueSnackbar(res.error?.data?.tittle ?? "Something went wrong, Unable to Duplicate", { variant: "error" });
            }
        });
        dispatch(closeBackdrop())
    };

    const ROWS = giveMeRows();

    const handleSortModelChange = React.useCallback((sortModel: GridSortModel) => {
        // Here you save the data you need from the sort model
        setQueryOptions({ sortModel: [...sortModel] });
    }, []);

    React.useEffect(() => {
        fetchQuotes();
        // eslint-disable-next-line
    }, [dropDown, paginationModel, JSON.stringify(queryOptions?.sortModel)]);

    React.useEffect(() => {
        const timeout = setTimeout(() => {
            fetchQuotes();
        }, 500)

        return () => clearTimeout(timeout)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    return (
        <MyPaper>
            <>
                <Stack gap={1} sx={{ flexDirection: { sm: "column", md: "row" } }} width={"100%"} alignItems="center">
                    <CurrentView entity={"quote"} />

                    <Stack flexGrow={1}>
                        {/* Header */}
                        <TitleHeader
                            title="Quick Quote"
                            count={ROWS?.length ?? 0}
                            showSearch={true}
                            search={search}
                            onSearchChange={setSearch}
                            searchPlaceholder={"Search for Quotes"}
                            showCreateBtn={creatable}
                            createBtnLabel={"Create Quote"}
                            onCreateBtn={() => addEditQuote(null, false)}
                            showDropDown={true}
                            onDropDownChange={(data: SelectBoxOption) => updateDropDown(data)}
                            dropDownLabel="Version"
                            dropDownOptions={entityHistoryOptions}
                            dropDownValue={dropDown}
                        />
                    </Stack>
                </Stack>

                {/* Error */}
                {isError && <Alert sx={{ mt: 2 }} severity="error">Oops! Something went wrong, Unable to fetch Quote. Try Again Later!</Alert>}


                {/* Data Grid */}
                <Box
                    sx={{
                        mt: 2,
                        height: "calc(100vh - 216px)"
                    }}>
                    <CustomDataGrid
                        id="project-list"
                        rows={ROWS}
                        columns={DashboardTableColumn(deleteQuote, addEditQuote, getPath, (row: any) => onPublish(row, fetchQuotes), !deletable, handleDuplicate, publishable)}
                        loading={isLoading || isFetching}
                        getRowId={(row) => row._id}
                        showToolbar={true}
                        rowCount={totalCount}
                        paginationModel={paginationModel}
                        paginationMode="server"
                        onPaginationModelChange={setPaginationModel}
                        sortingMode="server"
                        onSortModelChange={handleSortModelChange}
                    />
                </Box>
            </>
        </MyPaper>
    );
}